// El listado de observaciones postventa como datos: el estado que vive en
// la URL, el modelo de fila que alimenta la tabla compartida
// (components/ObservacionesTable.tsx), los filtros por columna con sus
// conteos y la situación del plazo del contratista. Puro y sin React:
// lo consumen el listado del proyecto y el archivo.
//
// Handoff design/incoming/design_handoff_listado_observaciones (README §2–4
// y §State Management). Filtrado y paginación en el cliente sobre el feed
// existente; server-side es follow-up.
//
// Ninguna etiqueta de estado ni de criticidad se escribe acá: salen del
// módulo compartido (`ESTADO_LABEL`, `CRITICIDAD_LABEL`) — fitness
// postventa-labels-single-source. Y ninguna clave de estado de la receta
// se compara: el estado de la fila es el `kind` presentacional que deriva
// phase.ts de la fase del engine (fitness observation-phase-single-source).

import {
  CRITICIDAD_LABEL,
  ESTADO_LABEL,
  especialidadLabel,
  type PostventaObservation,
  type RecipeMeResponse,
} from "@bitacorapro/api-types";
import { presentationalState, type Bucket, type PresentationalState } from "./phase";
import { resolveSeverityCode, type SeverityCode } from "./severity";
import { unidadDe } from "./unit";
import { avisoCriticoLabel } from "./avisoCritico";

// ── Estado de la URL ─────────────────────────────────────────────────

/** Los estados que ofrece el chip «Estado». `open` agrupa la
 *  solicitada nueva y la reabierta (comparten «Solicitada»; la
 *  reapertura es el modificador «Reabierta ×N»). Desde ADR-0141 el
 *  rechazo son tres: `rejected` (espera la respuesta del propietario),
 *  `appealed` (segunda revisión, cola del jefe) y `rejected_closed`
 *  (cerrada, va al archivo). */
export type EstadoFiltro =
  | "open"
  | "accepted"
  | "assigned"
  | "resolved"
  | "conforme"
  | "closed_no_response"
  | "rejected"
  | "appealed"
  | "rejected_closed";
export const ESTADOS_FILTRO: readonly EstadoFiltro[] = [
  "open",
  "accepted",
  "assigned",
  "resolved",
  "conforme",
  // ADR-0142 — «Cerrada por falta de respuesta».
  "closed_no_response",
  "rejected",
  "appealed",
  "rejected_closed",
];

/** Los grupos del KPI / del antiguo seg-toggle, expresados como estados:
 *  `?f=` de un enlace viejo se lee como estos estados. `pendientes` es
 *  «Pendientes de gestión»: lo que espera al jefe — por aceptar, por
 *  asignar Y la apelación (ADR-0141). Es lo que cuentan el KPI
 *  (`groupCounts`) y los rollups del servidor que enlazan con
 *  `?f=pendientes` (card del proyecto, badge del sidebar), así que el
 *  filtro deja exactamente las filas que el número contó. La apelada
 *  además tiene su grupo propio (`apeladas`). */
export const BUCKET_ESTADOS: Record<Exclude<Bucket, "all">, readonly EstadoFiltro[]> = {
  pendientes: ["open", "accepted", "appealed"],
  proveedor: ["assigned"],
  // Lo que espera al propietario: el trabajo listo por revisar y el
  // rechazo por responder (ADR-0141).
  conformidad: ["resolved", "rejected"],
  apeladas: ["appealed"],
  cerradas: ["conforme", "closed_no_response", "rejected_closed"],
};

export type CriticidadFiltro = SeverityCode | "sin";
export const CRITICIDADES_FILTRO: readonly CriticidadFiltro[] = ["S1", "S2", "S3", "S4", "sin"];

/** `sin` = asignada SIN vencimiento (anterior a la columna); `na` = no
 *  está en manos de un contratista (la celda muestra «—»). `na` es un
 *  valor interno: no es opción del chip «Plazo» ni se lee de la URL. */
export type SituacionPlazo = "sin" | "en_plazo" | "por_vencer" | "vencido" | "resuelta" | "na";
export const SITUACIONES_PLAZO: readonly Exclude<SituacionPlazo, "na">[] = [
  "sin",
  "en_plazo",
  "por_vencer",
  "vencido",
  "resuelta",
];
export const SITUACION_PLAZO_LABEL: Record<SituacionPlazo, string> = {
  na: "—",
  sin: "Sin plazo",
  en_plazo: "En plazo",
  por_vencer: "Vence en 2 días o menos",
  vencido: "Vencido",
  resuelta: "Resuelta",
};

export type Antiguedad = "lt7" | "7a14" | "15a30" | "gt30";
export const ANTIGUEDADES: readonly Antiguedad[] = ["lt7", "7a14", "15a30", "gt30"];
export const ANTIGUEDAD_LABEL: Record<Antiguedad, string> = {
  lt7: "Menos de 7 días",
  "7a14": "7 a 14 días",
  "15a30": "15 a 30 días",
  gt30: "Más de 30 días",
};

export const PAGE_SIZES = [10, 25, 50] as const;
export type PageSize = (typeof PAGE_SIZES)[number];
export const PAGE_SIZE_DEFAULT: PageSize = 10;

/** Las columnas filtrables. Cada una viaja en la URL con su propio
 *  nombre, como clave repetida (`?estado=open&estado=accepted`). */
export type FilterKey =
  | "estado"
  | "criticidad"
  | "unidad"
  | "contratista"
  | "especialidad"
  | "partida"
  | "antiguedad"
  | "plazo";
export const FILTER_KEYS: readonly FilterKey[] = [
  "estado",
  "criticidad",
  "unidad",
  "contratista",
  "especialidad",
  "partida",
  "antiguedad",
  "plazo",
];

export type ListState = Record<FilterKey, string[]> & {
  q: string;
  pagina: number;
  tam: PageSize;
};

const FILTER_DOMAIN: Partial<Record<FilterKey, readonly string[]>> = {
  estado: ESTADOS_FILTRO,
  criticidad: CRITICIDADES_FILTRO,
  antiguedad: ANTIGUEDADES,
  plazo: SITUACIONES_PLAZO,
};

/** El grupo de `?f=`. El alias `revisar` (la cola «Por revisar» que
 *  desapareció, decisión 4 del 2026-09-29) se normaliza a `pendientes`
 *  al leer y nunca se escribe de vuelta. Cualquier otro valor cae a
 *  «all». */
export function parseFiltro(raw: string | null): Bucket {
  if (raw === "revisar") return "pendientes";
  return raw === "pendientes" ||
    raw === "proveedor" ||
    raw === "conformidad" ||
    raw === "apeladas" ||
    raw === "cerradas"
    ? raw
    : "all";
}

function uniq(xs: string[]): string[] {
  return [...new Set(xs)];
}

/** Lee el estado del listado desde la URL. Tolerante: un valor fuera del
 *  dominio se descarta, una página inválida es la 1 y un tamaño fuera de
 *  10/25/50 es 10. Sin `?estado=`, un `?f=` heredado (enlaces del
 *  proyecto, de insights y del sidebar) se traduce a sus estados. */
export function parseListState(sp: URLSearchParams): ListState {
  const filters = {} as Record<FilterKey, string[]>;
  for (const k of FILTER_KEYS) {
    const dom = FILTER_DOMAIN[k];
    const vals = uniq(sp.getAll(k).filter((v) => v !== ""));
    filters[k] = dom ? vals.filter((v) => dom.includes(v)) : vals;
  }
  if (filters.estado.length === 0) {
    const f = parseFiltro(sp.get("f"));
    if (f !== "all") filters.estado = [...BUCKET_ESTADOS[f]];
  }
  const pagina = Number.parseInt(sp.get("pagina") ?? "", 10);
  const tam = Number.parseInt(sp.get("tam") ?? "", 10);
  return {
    ...filters,
    q: sp.get("q") ?? "",
    pagina: Number.isFinite(pagina) && pagina >= 1 ? pagina : 1,
    tam: (PAGE_SIZES as readonly number[]).includes(tam) ? (tam as PageSize) : PAGE_SIZE_DEFAULT,
  };
}

export type ListPatch = Partial<ListState>;

/** Escribe un cambio sobre la URL previa. Cada clave en su default se
 *  BORRA (arrays vacíos, q "", página 1, tamaño 10), así la URL limpia es
 *  la canónica. Cualquier cambio que no sea de página vuelve a la 1.
 *  Tocar `estado` reemplaza el `?f=` heredado; si no se toca, `?f=` se
 *  conserva tal cual (un enlace que sólo quita la partida no reescribe
 *  el grupo). */
export function writeListState(prev: URLSearchParams, patch: ListPatch): URLSearchParams {
  const next = new URLSearchParams(prev);
  for (const k of FILTER_KEYS) {
    const v = patch[k];
    if (v === undefined) continue;
    next.delete(k);
    if (k === "estado") next.delete("f");
    for (const x of uniq(v)) next.append(k, x);
  }
  if (patch.q !== undefined) {
    if (patch.q === "") next.delete("q");
    else next.set("q", patch.q);
  }
  if (patch.tam !== undefined) {
    if (patch.tam === PAGE_SIZE_DEFAULT) next.delete("tam");
    else next.set("tam", String(patch.tam));
  }
  const tocaFiltro =
    FILTER_KEYS.some((k) => patch[k] !== undefined) ||
    patch.q !== undefined ||
    patch.tam !== undefined;
  if (patch.pagina !== undefined) {
    if (patch.pagina <= 1) next.delete("pagina");
    else next.set("pagina", String(patch.pagina));
  } else if (tocaFiltro) {
    next.delete("pagina");
  }
  return next;
}

/** La URL canónica de la paginación: si `?pagina=` / `?tam=` crudos no
 *  coinciden con lo que se pinta (`?pagina=abc`, `?tam=999`, una página
 *  fuera de rango ya acotada a `pagina`, claves repetidas), devuelve la
 *  URL corregida para un `replace`; null si ya es canónica. */
export function normalizePagingParams(sp: URLSearchParams, pagina: number): URLSearchParams | null {
  const { tam } = parseListState(sp);
  const next = writeListState(sp, { pagina, tam });
  const igual = (k: string) => sp.getAll(k).length <= 1 && sp.get(k) === next.get(k);
  return igual("pagina") && igual("tam") ? null : next;
}

/** Vacía todos los filtros y la búsqueda (no el tamaño de página). */
export const CLEAR_FILTERS: ListPatch = {
  estado: [],
  criticidad: [],
  unidad: [],
  contratista: [],
  especialidad: [],
  partida: [],
  antiguedad: [],
  plazo: [],
  q: "",
};

export function hasActiveFilters(s: ListState): boolean {
  return s.q.trim() !== "" || FILTER_KEYS.some((k) => s[k].length > 0);
}

// ── Fechas y plazo ───────────────────────────────────────────────────

const DAY_MS = 86_400_000;

/** Los días del plazo se cuentan en el calendario de Chile, no en la zona
 *  del navegador: el correo al contratista pinta el vencimiento como
 *  fecha en America/Santiago y el listado tiene que cuadrar con él. */
export const PLAZO_TIME_ZONE = "America/Santiago";

const YMD = new Intl.DateTimeFormat("en-CA", {
  timeZone: PLAZO_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** El día calendario en Chile, como medianoche UTC de esa fecha (sirve
 *  sólo para restar días; DST no la mueve). */
function diaChile(d: Date): number {
  const p = YMD.formatToParts(d);
  const n = (t: Intl.DateTimeFormatPartTypes) =>
    Number(p.find((x) => x.type === t)?.value ?? Number.NaN);
  return Date.UTC(n("year"), n("month") - 1, n("day"));
}

/** Días calendario (en America/Santiago) de `from` a `to`; negativo si
 *  `to` es anterior. */
export function diasCalendario(from: Date, to: Date): number {
  return Math.round((diaChile(to) - diaChile(from)) / DAY_MS);
}

const FECHA_CORTA = new Intl.DateTimeFormat("es-CL", {
  day: "2-digit",
  month: "short",
  timeZone: PLAZO_TIME_ZONE,
});

/** «25-sept»: día y mes abreviado en español, sin punto final. */
export function fechaCorta(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return FECHA_CORTA.format(d).replace(/\./g, "").replace(/\s+/g, "-");
}

function dias(n: number): string {
  return `${n} ${n === 1 ? "día" : "días"}`;
}

export type PlazoTone = "ok" | "info" | "warn" | "crit" | "idle";

export type PlazoInfo = {
  situacion: SituacionPlazo;
  /** Texto de la pill; null = sin pill (la celda muestra `vacio`). */
  pill: string | null;
  tone: PlazoTone;
  /** Texto en la celda cuando no hay pill. */
  vacio: string;
  /** Días que faltan (negativo = vencido); null sin vencimiento. */
  diasRestantes: number | null;
};

/** La situación del plazo del contratista para una fila, con `now`
 *  inyectado (días corridos, a medianoche de Chile):
 *  - resuelta: el contratista terminó (lista para revisar), se cerró
 *    con la conformidad del propietario o por falta de respuesta
 *    (ADR-0142: el trabajo del contratista se terminó igual);
 *  - na: no está en manos de un contratista (celda «—»);
 *  - sin: asignada antes de que existiera el plazo (sin `dueAt`);
 *  - vencido / por_vencer (0..2 días) / en_plazo (más de 2). */
export function situacionPlazo(
  o: Pick<PostventaObservation, "dueAt" | "observationStatusAt" | "observationClosedAt">,
  state: Pick<PresentationalState, "kind">,
  now: Date
): PlazoInfo {
  if (
    state.kind === "resolved" ||
    state.kind === "conforme" ||
    state.kind === "closed_no_response"
  ) {
    // El cierre sin respuesta no mueve `observationStatusAt`: sigue
    // siendo el día en que el contratista lo dejó listo.
    const fecha =
      state.kind === "conforme"
        ? (o.observationClosedAt ?? o.observationStatusAt)
        : o.observationStatusAt;
    return {
      situacion: "resuelta",
      pill: fecha ? `Resuelta ${fechaCorta(fecha)}` : "Resuelta",
      tone: "ok",
      vacio: "",
      diasRestantes: null,
    };
  }
  if (state.kind !== "assigned") {
    return { situacion: "na", pill: null, tone: "idle", vacio: "—", diasRestantes: null };
  }
  const due = o.dueAt ? new Date(o.dueAt) : null;
  if (!due || Number.isNaN(due.getTime())) {
    return {
      situacion: "sin",
      pill: null,
      tone: "idle",
      vacio: "Sin plazo",
      diasRestantes: null,
    };
  }
  const restantes = diasCalendario(now, due);
  if (restantes < 0) {
    return {
      situacion: "vencido",
      pill: `Vencido hace ${dias(-restantes)}`,
      tone: "crit",
      vacio: "",
      diasRestantes: restantes,
    };
  }
  if (restantes <= 2) {
    return {
      situacion: "por_vencer",
      pill: restantes === 0 ? "Vence hoy" : `Vence en ${dias(restantes)}`,
      tone: "warn",
      vacio: "",
      diasRestantes: restantes,
    };
  }
  return {
    situacion: "en_plazo",
    pill: `En plazo · ${fechaCorta(o.dueAt)}`,
    tone: "info",
    vacio: "",
    diasRestantes: restantes,
  };
}

/** ADR-0142 — «Se cierra 05-nov»: la fecha de cierre previsto de una
 *  observación que espera al propietario. La proyecta el servidor
 *  (`autoApprovalAt`); sin ella (job apagado, veto) no hay texto. */
export function cierrePrevistoFila(
  o: Pick<PostventaObservation, "autoApprovalAt">,
  state: Pick<PresentationalState, "kind">
): string | null {
  if (state.kind !== "resolved" || !o.autoApprovalAt) return null;
  return `Se cierra ${fechaCorta(o.autoApprovalAt)}`;
}

export function antiguedadDe(diasDesdeReporte: number): Antiguedad {
  if (diasDesdeReporte < 7) return "lt7";
  if (diasDesdeReporte <= 14) return "7a14";
  if (diasDesdeReporte <= 30) return "15a30";
  return "gt30";
}

/** «Hoy» o «hace N días». */
export function antiguedadLabel(diasDesdeReporte: number): string {
  return diasDesdeReporte <= 0 ? "Hoy" : `hace ${dias(diasDesdeReporte)}`;
}

// ── Modelo de fila ───────────────────────────────────────────────────

export const SIN_CONTRATISTA = "__sin__";

/** Respaldo de la etiqueta de un `?unidad=` que no se puede resolver: el
 *  UUID nunca se le muestra al jefe. */
export const UNIDAD_SELECCIONADA = "Unidad seleccionada";

export type FilaObs = {
  o: PostventaObservation;
  state: PresentationalState;
  titulo: string;
  recinto: string;
  estado: EstadoFiltro;
  sev: SeverityCode | null;
  unidadId: string;
  unidad: string;
  /** Contratista VIGENTE: una fila que volvió a triage o a aceptada
   *  conserva el proveedor de un ciclo anterior, que ya no es el suyo. */
  contratistaId: string;
  contratista: string | null;
  especialidadKey: string;
  especialidad: string | null;
  partidaCode: string | null;
  partida: string | null;
  plazo: PlazoInfo;
  diasDesdeReporte: number;
  antiguedad: Antiguedad;
  /** Lo que recorre la búsqueda, en minúsculas. */
  haystack: string;
  /** ADR-0156 — «Avisada al jefe 10:42» bajo el pill de criticidad. */
  avisoCritico: string | null;
};

export type FilaExtras = {
  /** Nombre de la especialidad de taxonomía (`specialtyCode`): entra a
   *  la búsqueda. */
  especialidadTaxonomia?: (code: string | null | undefined) => string | null;
  /** Respaldo del nombre de partida cuando el feed no trae
   *  `partidaNombre` (core-api anterior al listado). */
  partidaNombre?: (code: string) => string | null;
};

export function buildFilas(
  observations: readonly PostventaObservation[],
  recipe: Pick<RecipeMeResponse, "findingCategories" | "observationStatuses"> | null | undefined,
  now: Date,
  extras: FilaExtras = {}
): FilaObs[] {
  return observations.map((o) => {
    const state = presentationalState(o, recipe ?? undefined);
    const titulo = o.aiTitle ?? o.humanDescription ?? o.aiDescription ?? "—";
    const recinto = o.recintoNombre ?? o.areaName;
    const sev = resolveSeverityCode(o);
    const unidad = o.assetUnit ?? unidadDe(o.assetLabel);
    const vigente = state.kind !== "open" && state.kind !== "reopened" && state.kind !== "accepted";
    const provider = vigente ? (o.provider ?? null) : null;
    const especialidad = especialidadLabel(recipe?.findingCategories ?? [], o.category);
    const partidaCode = o.itemCode ?? null;
    const partida = partidaCode
      ? (o.partidaNombre ?? extras.partidaNombre?.(partidaCode) ?? partidaCode)
      : null;
    const diasDesdeReporte = Math.max(0, diasCalendario(new Date(o.createdAt), now));
    const haystack = [
      titulo,
      o.humanDescription,
      o.aiDescription,
      recinto,
      unidad,
      o.assetLabel,
      partida,
      especialidad,
      extras.especialidadTaxonomia?.(o.specialtyCode),
      provider?.shortName,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return {
      o,
      state,
      titulo,
      recinto,
      estado: state.kind === "reopened" ? "open" : state.kind,
      sev,
      unidadId: o.assetId,
      unidad,
      contratistaId: provider?.id ?? SIN_CONTRATISTA,
      contratista: provider?.shortName ?? null,
      especialidadKey: o.category ?? "",
      especialidad,
      partidaCode,
      partida,
      plazo: situacionPlazo(o, state, now),
      diasDesdeReporte,
      antiguedad: antiguedadDe(diasDesdeReporte),
      haystack,
      avisoCritico: avisoCriticoLabel(o.criticalAlert),
    };
  });
}

/** El valor de una fila en una columna filtrable. */
export function valueOf(f: FilaObs, k: FilterKey): string {
  switch (k) {
    case "estado":
      return f.estado;
    case "criticidad":
      return f.sev ?? "sin";
    case "unidad":
      return f.unidadId;
    case "contratista":
      return f.contratistaId;
    case "especialidad":
      return f.especialidadKey;
    case "partida":
      return f.partidaCode ?? "";
    case "antiguedad":
      return f.antiguedad;
    case "plazo":
      return f.plazo.situacion;
  }
}

/** Filtra con todo el estado salvo, opcionalmente, una columna (la del
 *  panel que se está contando). */
export function applyFilters(filas: readonly FilaObs[], s: ListState, skip?: FilterKey): FilaObs[] {
  const ql = s.q.trim().toLowerCase();
  const activos = FILTER_KEYS.filter((k) => k !== skip && s[k].length > 0);
  return filas.filter((f) => {
    for (const k of activos) if (!s[k].includes(valueOf(f, k))) return false;
    return !ql || f.haystack.includes(ql);
  });
}

export type FilterOption = { value: string; label: string; count: number };

/** Las opciones de un chip con su conteo contra los OTROS filtros. Los
 *  dominios fijos (estado, criticidad, antigüedad, plazo) listan todas
 *  sus opciones aunque cuenten 0; los abiertos (unidad, contratista,
 *  especialidad, partida) sólo los valores presentes en `filas`. */
export function optionsFor(
  filas: readonly FilaObs[],
  s: ListState,
  k: FilterKey,
  domain?: readonly string[],
  /** Etiqueta de un valor que no aparece en ninguna fila (p. ej. un
   *  `?unidad=<uuid>` de una unidad sin observaciones). Null = el
   *  respaldo genérico de la columna. */
  labelFallback?: (value: string) => string | null | undefined
): FilterOption[] {
  const counts = new Map<string, number>();
  for (const f of applyFilters(filas, s, k)) {
    const v = valueOf(f, k);
    counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  const labelOf = (v: string): string => {
    switch (k) {
      case "estado":
        return ESTADO_LABEL[v as EstadoFiltro];
      case "criticidad":
        return v === "sin" ? "Sin criticidad" : CRITICIDAD_LABEL[v as SeverityCode];
      case "antiguedad":
        return ANTIGUEDAD_LABEL[v as Antiguedad];
      case "plazo":
        return SITUACION_PLAZO_LABEL[v as SituacionPlazo];
      default: {
        const f = filas.find((x) => valueOf(x, k) === v);
        if (!f) {
          const fb = labelFallback?.(v);
          if (fb) return fb;
        }
        if (k === "unidad") return f?.unidad ?? UNIDAD_SELECCIONADA;
        if (k === "contratista") return f?.contratista ?? "Sin contratista";
        if (k === "especialidad") return f?.especialidad ?? "Sin especialidad";
        return f?.partida ?? v;
      }
    }
  };
  let values: string[];
  const fixed = domain ?? FILTER_DOMAIN[k];
  if (fixed) {
    values = [...fixed];
  } else {
    values = uniq(filas.map((f) => valueOf(f, k)).filter((v) => k !== "partida" || v !== ""));
    values.sort((a, b) =>
      k === "contratista" && a === SIN_CONTRATISTA
        ? 1
        : k === "contratista" && b === SIN_CONTRATISTA
          ? -1
          : labelOf(a).localeCompare(labelOf(b), "es", { numeric: true })
    );
  }
  // Un valor de la URL que ya no está entre las filas sigue visible y
  // marcado, para poder quitarlo.
  for (const v of s[k]) if (!values.includes(v)) values.push(v);
  return values.map((v) => ({ value: v, label: labelOf(v), count: counts.get(v) ?? 0 }));
}

// ── Paginación ───────────────────────────────────────────────────────

export function totalPages(total: number, tam: number): number {
  return Math.max(1, Math.ceil(total / tam));
}

/** La página efectiva: la de la URL acotada a las que existen (si los
 *  filtros dejan la actual vacía, la última disponible). */
export function clampPage(pagina: number, total: number, tam: number): number {
  return Math.min(Math.max(1, pagina), totalPages(total, tam));
}

export function pageSlice<T>(rows: readonly T[], pagina: number, tam: number): T[] {
  const start = (pagina - 1) * tam;
  return rows.slice(start, start + tam);
}
